import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { StatFilterRange, StatsFilterRanges } from "./types";
import type { SelectedColumnsState } from "./types";
import { FilterKeys } from "./types";
import { DEFAULT_PAGE_SIZE, STATS_FILTERS } from "./utils/constants";

const EXPECTED_GOALS_SLIDER_RANGE: StatFilterRange = [0, 5];

const DEFAULT_RANGE_BY_KEY: Record<string, StatFilterRange> = {
	[FilterKeys.MINUTES]: [90, 900],
	[FilterKeys.GAMES]: [1, 100],
	[FilterKeys.GOALS]: [0, 100],
	[FilterKeys.TOTAL_XG]: EXPECTED_GOALS_SLIDER_RANGE,
	[FilterKeys.TOTAL_XA]: EXPECTED_GOALS_SLIDER_RANGE,
	[FilterKeys.NON_PENALTY_XG]: EXPECTED_GOALS_SLIDER_RANGE,
	[FilterKeys.NON_PENALTY_XG_PER_SHOT]: EXPECTED_GOALS_SLIDER_RANGE,
	[FilterKeys.NON_PENALTY_XG_PLUS_XA_PER_90]: EXPECTED_GOALS_SLIDER_RANGE,
	[FilterKeys.GOALS_XG_RATIO]: EXPECTED_GOALS_SLIDER_RANGE,
};

/** Default range for a stat key (percentile 0–100 or key-specific bounds). */
export function getDefaultRangeForKey(key: string): StatFilterRange {
	return DEFAULT_RANGE_BY_KEY[key] ?? [0, 100];
}

/** Default ranges for all STATS_FILTERS keys. Single source of truth for store and API default. */
export function getDefaultStatsFilterRanges(): StatsFilterRanges {
	const ranges: StatsFilterRanges = {};
	STATS_FILTERS.forEach((key: string) => {
		ranges[key] = getDefaultRangeForKey(key);
	});
	return ranges;
}

export type SeasonFiltersState = {
	seasons?: number;
	selectedRange?: { start: string; end: string };
} | null;

export type PaginationState = { page: number; limit: number };

const STORAGE_VERSION = 2;
const PERSIST_NAME = "players-grid-stats-filters";

type State = {
	ranges: StatsFilterRanges;
	selectedPresetId: string;
	_storageVersion: number;
	// Persisted UI state (migrated from local storage)
	leftFilters: Record<string, unknown> | null;
	seasonFilters: SeasonFiltersState;
	pagination: PaginationState;
	selectedColumnsState: SelectedColumnsState | null;
	selectedColumnsId: string;
};

type Actions = {
	setRanges: (ranges: StatsFilterRanges) => void;
	setRange: (statKey: string, range: StatFilterRange) => void;
	setSelectedPresetId: (id: string) => void;
	setLeftFilters: (value: Record<string, unknown> | null) => void;
	setSeasonFilters: (value: SeasonFiltersState) => void;
	setPagination: (value: PaginationState) => void;
	setSelectedColumnsState: (value: SelectedColumnsState | null) => void;
	setSelectedColumnsId: (id: string) => void;
	reset: () => void;
	resetStatsFilters: () => void;
	clearFilters: () => void;
	hasActiveFilters: () => boolean;
};

const defaultPagination: PaginationState = { page: 0, limit: DEFAULT_PAGE_SIZE };

const initialState: State = {
	ranges: getDefaultStatsFilterRanges(),
	selectedPresetId: "",
	_storageVersion: STORAGE_VERSION,
	leftFilters: null,
	seasonFilters: null,
	pagination: defaultPagination,
	selectedColumnsState: null,
	selectedColumnsId: "General",
};

export const useStatsFiltersStore = create<State & Actions>()(
	persist(
		(set, get) => ({
			...initialState,
			setRanges: (ranges) => set({ ranges: { ...getDefaultStatsFilterRanges(), ...ranges } }),
			setRange: (statKey, range) =>
				set((state) => ({
					ranges: { ...state.ranges, [statKey]: range },
				})),
			setSelectedPresetId: (id) => set({ selectedPresetId: id ?? "" }),
			setLeftFilters: (value) => set({ leftFilters: value }),
			setSeasonFilters: (value) => set({ seasonFilters: value }),
			setPagination: (value) => set({ pagination: value }),
			setSelectedColumnsState: (value) => set({ selectedColumnsState: value }),
			setSelectedColumnsId: (id) => set({ selectedColumnsId: id ?? "General" }),
			reset: () =>
				set({
					ranges: getDefaultStatsFilterRanges(),
					selectedPresetId: "",
					_storageVersion: STORAGE_VERSION,
					leftFilters: null,
					seasonFilters: null,
					pagination: defaultPagination,
					selectedColumnsState: null,
					selectedColumnsId: "General",
				}),
			resetStatsFilters: () =>
				set({
					ranges: getDefaultStatsFilterRanges(),
					selectedPresetId: "",
					_storageVersion: STORAGE_VERSION,
					pagination: defaultPagination,
				}),
			clearFilters: () =>
				set({
					leftFilters: null,
					seasonFilters: null,
					pagination: defaultPagination,
				}),
			hasActiveFilters: () =>
				get().leftFilters !== null ||
				get().seasonFilters !== null ||
				STATS_FILTERS.some((key) => {
					const range = get().ranges[key];
					const def = getDefaultRangeForKey(key);
					return range && (range[0] !== def[0] || range[1] !== def[1]);
				}),
		}),
		{
			name: PERSIST_NAME,
			partialize: (state) => ({
				ranges: state.ranges,
				selectedPresetId: state.selectedPresetId,
				_storageVersion: state._storageVersion,
				leftFilters: state.leftFilters,
				seasonFilters: state.seasonFilters,
				pagination: state.pagination,
				selectedColumnsState: state.selectedColumnsState,
				selectedColumnsId: state.selectedColumnsId,
			}),
			version: STORAGE_VERSION,
		}
	)
);
